import { Injectable } from '@angular/core';
// FIX: Corrected import path for models.
import { Product } from '../models/index';
import { MOCK_PRODUCTS } from '../data/mock-data';
import { getSupabase, isSupabaseConfigured } from '../firebase';
import { SupabaseClient } from '@supabase/supabase-js';

const PRODUCT_TABLE = 'products';

@Injectable({ providedIn: 'root' })
export class ProductService {
  private supabase: SupabaseClient | null = getSupabase();

  private toProductDB(product: Partial<Product>): any {
    return {
      id: product.id,
      store_id: product.storeId,
      sku: product.sku,
      barcode: product.barcode,
      name: product.name,
      category_id: product.categoryId,
      is_prescription_required: product.isPrescriptionRequired,
      is_taxable: product.isTaxable,
      strain_profile: product.strainProfile,
      is_composite: product.isComposite,
      composition: product.composition,
      price: product.price,
      cost: product.cost,
      stock_quantity: product.stockQuantity,
      unit: product.unit,
      "imageUrl": product.imageUrl,
      active: product.active,
    };
  }

  async getProducts(): Promise<Product[]> {
    if (!isSupabaseConfigured() || !this.supabase) {
        return Promise.resolve(MOCK_PRODUCTS);
    }
    // RLS handles filtering by store_id automatically
    const { data, error } = await this.supabase.from(PRODUCT_TABLE).select('*, category:categories(name)');
    if (error) throw error;
    return (data || []).map((p: any) => ({
      ...p,
      storeId: p.store_id,
      categoryId: p.category_id,
      categoryName: p.category?.name || 'Uncategorized',
      stockQuantity: p.stock_quantity,
      isPrescriptionRequired: p.is_prescription_required,
      isTaxable: p.is_taxable,
      strainProfile: p.strain_profile,
      isComposite: p.is_composite,
      createdAt: p.created_at
    })) as Product[];
  }
  
  async addProduct(productData: Omit<Product, 'id' | 'createdAt'>): Promise<Product> {
    if (!isSupabaseConfigured() || !this.supabase) {
      const newProduct: Product = { ...productData, id: `mock-${Date.now()}`, createdAt: new Date().toISOString() };
      return Promise.resolve(newProduct);
    }
    const dbPayload = this.toProductDB(productData);
    delete (dbPayload as any).id;
    const { data, error } = await this.supabase.from(PRODUCT_TABLE).insert(dbPayload).select().single();
    if (error) throw error;
    return data as Product;
  }

  async updateProduct(updatedProduct: Product): Promise<Product> {
    if (!isSupabaseConfigured() || !this.supabase) {
        return Promise.resolve(updatedProduct);
    }
    const { id, ...productData } = updatedProduct;
    const dbPayload = this.toProductDB(productData);
    const { data, error } = await this.supabase.from(PRODUCT_TABLE).update(dbPayload).eq('id', id).select().single();
    if (error) throw error;
    return data as Product;
  }

  async deleteProduct(productId: string): Promise<void> {
    if (!isSupabaseConfigured() || !this.supabase) {
        return Promise.resolve();
    }
    const { error } = await this.supabase.from(PRODUCT_TABLE).delete().eq('id', productId);
    if (error) throw error;
  }
  
  async batchAddProducts(products: Omit<Product, 'id' | 'createdAt'>[]): Promise<{ success: boolean; message: string }> {
    if (!isSupabaseConfigured() || !this.supabase) {
      return { success: false, message: 'Database not connected.' };
    }
    try {
      const { error: deleteError } = await this.supabase.from(PRODUCT_TABLE).delete().neq('id', '00000000-0000-0000-0000-000000000000');
      if (deleteError) throw deleteError;
      const productsToInsert = products.map(p => this.toProductDB(p));
      const { error: insertError } = await this.supabase.from(PRODUCT_TABLE).insert(productsToInsert);
      if (insertError) throw insertError;
      return { success: true, message: `${products.length} products imported successfully.` };
    } catch (e) {
      return { success: false, message: e instanceof Error ? e.message : 'Unknown error' };
    }
  }
}
